/**
 * Content for Trust & Conversion pages (Phase 5).
 * Purely bilingual data — pages are presentational.
 * The team can extend this file to add new founders, values, roadmap items,
 * press mentions or partner categories without touching page code.
 */

export const VALUES = [
  { key: "hospitality_first", en: "Hospitality first", nl: "Hospitality eerst", body: { en: "Every design decision starts with the hotel — its team, its guests, its rhythm.", nl: "Elke ontwerpkeuze begint bij het hotel — het team, de gasten, het ritme." } },
  { key: "trust", en: "Trust", nl: "Vertrouwen", body: { en: "We handle guest data like your reputation depends on it — because it does.", nl: "We behandelen gastdata alsof uw reputatie ervan afhangt — want dat is zo." } },
  { key: "innovation", en: "Innovation", nl: "Innovatie", body: { en: "AI, automation and new interfaces — always in service of hospitality, never above it.", nl: "AI, automatisering en nieuwe interfaces — altijd in dienst van hospitality." } },
  { key: "security", en: "Security", nl: "Security", body: { en: "Enterprise-grade by default. Zero-trust, encryption, quarterly pentests.", nl: "Enterprise-grade by default. Zero-trust, encryptie, elk kwartaal pentests." } },
  { key: "partnership", en: "Long-term partnerships", nl: "Langetermijn-partnerships", body: { en: "We're building for the next decade. Every roadmap decision reflects that.", nl: "We bouwen voor het volgende decennium. Elke roadmap-beslissing weerspiegelt dat." } },
  { key: "customer_success", en: "Customer success", nl: "Customer success", body: { en: "Your rollout, adoption and daily use matter more than any feature checklist.", nl: "Uw uitrol, adoptie en dagelijks gebruik doen er meer toe dan elke feature-checklist." } },
  { key: "continuous", en: "Continuous improvement", nl: "Continue verbetering", body: { en: "Small, sharp releases every week — driven by hoteliers using BHT One today.", nl: "Kleine, scherpe releases elke week — gedreven door hoteliers die BHT One vandaag gebruiken." } },
];

export const TRUST_SIGNALS = [
  { icon: "hoteliers", en: "Built by hoteliers", nl: "Gebouwd door hoteliers" },
  { icon: "privacy", en: "Privacy first", nl: "Privacy first" },
  { icon: "security", en: "Security first", nl: "Security first" },
  { icon: "ai", en: "AI transparency", nl: "AI-transparantie" },
  { icon: "eu", en: "European hosting", nl: "Europese hosting" },
  { icon: "support", en: "Reliable support", nl: "Betrouwbare support" },
  { icon: "dev", en: "Continuous development", nl: "Continue ontwikkeling" },
  { icon: "vision", en: "Long-term vision", nl: "Langetermijnvisie" },
];

// Founder placeholder: real bios & portraits will be added.
export const FOUNDERS = [
  {
    id: "founder-1",
    name: "Founder placeholder",
    role: { en: "Founder & CEO — Hospitality operations", nl: "Founder & CEO — Hospitality operations" },
    bio: {
      en: "Real biography coming soon. We're preparing professional portraits and full profiles for every founder and team member.",
      nl: "Echte biografie komt binnenkort. We bereiden professionele portretten en volledige profielen voor voor elke founder en teamlid.",
    },
    linkedin: null,
  },
  {
    id: "founder-2",
    name: "Founder placeholder",
    role: { en: "Co-founder & CTO — Software engineering", nl: "Co-founder & CTO — Software engineering" },
    bio: {
      en: "Real biography coming soon.",
      nl: "Echte biografie komt binnenkort.",
    },
    linkedin: null,
  },
];

// Team departments (for the future team scale)
export const TEAM_DEPARTMENTS = [
  { key: "engineering", en: "Engineering", nl: "Engineering" },
  { key: "hospitality", en: "Hospitality Success", nl: "Hospitality Success" },
  { key: "customer_success", en: "Customer Success", nl: "Customer Success" },
  { key: "design", en: "Design", nl: "Design" },
  { key: "marketing", en: "Marketing", nl: "Marketing" },
  { key: "support", en: "Support", nl: "Support" },
];

// Case-study framework — each entry drives a full case-study page.
// Currently a single "coming soon" placeholder that authentically communicates
// "our first pilots will fill this in".
export const CASE_STUDIES = [
  {
    slug: "first-pilot-coming-soon",
    published: false,
    en: {
      title: "Our first pilot hotels · going live shortly",
      excerpt: "We're onboarding our first pilot hotels. Real names, real numbers and real quotes will appear here — no invented case studies, ever.",
      hotel: { name: "Coming soon", type: "Coming soon", location: "Europe", rooms: "—", website: null, since: "2026" },
      sections: {
        introduction: "This case study is being prepared with our first pilot hotel. We'll publish the story the moment the general manager approves the numbers and the quote.",
        challenges: [],
        why_bht: [],
        implementation: "",
        modules: [],
        daily_improvements: [],
        adoption: "",
        ai_usage: "",
        knowledge_base: "",
        internal_comm: "",
        website_app: "",
        impact: [],
        quote: { text: "", author: "", role: "" },
        roadmap: "",
      },
    },
    nl: {
      title: "Onze eerste pilot-hotels · binnenkort live",
      excerpt: "We onboarden onze eerste pilot-hotels. Echte namen, echte cijfers en echte quotes verschijnen hier — geen verzonnen case studies, ooit.",
      hotel: { name: "Binnenkort", type: "Binnenkort", location: "Europa", rooms: "—", website: null, since: "2026" },
      sections: {
        introduction: "Deze case study wordt voorbereid met ons eerste pilot-hotel. We publiceren het verhaal zodra de general manager de cijfers en het citaat heeft goedgekeurd.",
        challenges: [],
        why_bht: [],
        implementation: "",
        modules: [],
        daily_improvements: [],
        adoption: "",
        ai_usage: "",
        knowledge_base: "",
        internal_comm: "",
        website_app: "",
        impact: [],
        quote: { text: "", author: "", role: "" },
        roadmap: "",
      },
    },
  },
];

// KPI placeholders for the framework
export const CASE_KPI_TEMPLATE = [
  { key: "hours_saved", en: "Hours saved per week", nl: "Uur bespaard per week" },
  { key: "manual_admin", en: "Reduced manual administration", nl: "Minder handmatige administratie" },
  { key: "housekeeping_speed", en: "Faster housekeeping coordination", nl: "Snellere housekeeping-coördinatie" },
  { key: "onboarding_speed", en: "Faster employee onboarding", nl: "Snellere onboarding" },
  { key: "guest_satisfaction", en: "Guest satisfaction improvement", nl: "Verbetering gasttevredenheid" },
  { key: "operational_efficiency", en: "Operational efficiency uplift", nl: "Operationele efficiëntie" },
  { key: "response_times", en: "Reduced response times", nl: "Snellere reactietijden" },
];

// Roadmap for Investor Relations
export const ROADMAP = [
  { quarter: "2026 · Q1", en: "First pilot hotels · foundation platform live", nl: "Eerste pilot-hotels · fundament platform live" },
  { quarter: "2026 · Q2", en: "Guest app · deeper F&B · marketing module", nl: "Gast-app · uitgebreide F&B · marketing-module" },
  { quarter: "2026 · Q3", en: "Multi-property portfolio dashboards", nl: "Multi-property portfolio-dashboards" },
  { quarter: "2026 · Q4", en: "Voice · AI concierge · SOC 2 Type II", nl: "Voice · AI-concierge · SOC 2 Type II" },
  { quarter: "2027 · H1", en: "International expansion · APAC data residency", nl: "Internationale expansie · APAC data-residency" },
];

// Manifesto principles
export const MANIFESTO = [
  { en: "Hotels are built around people. Not software.", nl: "Hotels zijn gebouwd rond mensen. Niet rond software." },
  { en: "Technology should reduce complexity — never add to it.", nl: "Technologie moet complexiteit wegnemen — nooit toevoegen." },
  { en: "Technology should never replace genuine hospitality.", nl: "Technologie mag echte gastvrijheid nooit vervangen." },
  { en: "Technology should create more time for employees.", nl: "Technologie moet meer tijd creëren voor medewerkers." },
  { en: "Technology should preserve knowledge.", nl: "Technologie moet kennis bewaren." },
  { en: "Technology should improve collaboration between departments.", nl: "Technologie moet samenwerking tussen afdelingen verbeteren." },
  { en: "Technology should simplify operations.", nl: "Technologie moet operatie vereenvoudigen." },
  { en: "Technology should quietly support every department.", nl: "Technologie moet elke afdeling stil ondersteunen." },
  { en: "The best hotel technology is the one your team forgets it is using.", nl: "De beste hoteltechnologie is die waarvan uw team vergeet dat ze het gebruikt." },
];

export const PARTNER_CATEGORIES = [
  { key: "consultants", en: "Hotel consultants", nl: "Hotelconsultants" },
  { key: "agencies", en: "Hospitality agencies", nl: "Hospitality-bureaus" },
  { key: "pms", en: "PMS partners", nl: "PMS-partners" },
  { key: "channel", en: "Channel managers", nl: "Channel managers" },
  { key: "technology", en: "Technology partners", nl: "Technologiepartners" },
  { key: "integrators", en: "System integrators", nl: "System integrators" },
];
